/**
 * The staffing-constraint familiarity scale, rendered (EPIC-043).
 *
 * The 1-5 rating is **ordinal** — an ordered position, not an identity — so it
 * takes ONE hue with monotone lightness steps (`--rating-1` … `--rating-5`),
 * never the `--chart-*` series, which shift hue and would read as five
 * unrelated categories rather than a scale. Dark mode is a selected ramp rather
 * than a flip: on a dark surface "more" reads as brighter, so it runs the other
 * way. Both were validated with the dataviz ordinal checks — see the tokens in
 * `styles/globals.css` before changing a step.
 *
 * **Colour is always redundant here.** Every use pairs the swatch with the
 * number and, where there is room, the rung's name — so the scale never depends
 * on colour vision, and a colour-blind or printed reader loses nothing.
 */

/** `--rating-N` for a 1-5 rung, clamped so a bad value can't produce `var(--rating-undefined)`. */
export function ratingColorVar(level: number): string {
  const clamped = Math.min(5, Math.max(1, Math.round(level)));
  return `var(--rating-${clamped})`;
}

/**
 * A rating as a coloured swatch plus its number, and optionally the rung name.
 *
 * Used on the constraints report and the person profile so the same number
 * looks the same in both places — two views of one fact reading differently is
 * worse than either style on its own.
 */
export function RatingBadge({
  level,
  name,
  className = "",
}: {
  level: number;
  /** The rung's admin-editable name, shown beside the number when given. */
  name?: string | null;
  className?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap ${className}`}>
      <span
        aria-hidden
        className="size-2.5 shrink-0 rounded-[2px]"
        style={{ backgroundColor: ratingColorVar(level) }}
      />
      <span className="font-medium text-sm tabular-nums">{level}</span>
      {name && <span className="text-muted-foreground text-xs">{name}</span>}
    </span>
  );
}

/**
 * A constraint's rung distribution as one thin stacked bar — the glanceable
 * form of the coverage panel, for the master list.
 *
 * Segments run 1→5 left to right so the bar reads as the scale itself, and each
 * carries a `title` so identity is never colour-alone on hover; the full
 * labelled breakdown is one click away in the detail panel. A 2px surface gap
 * separates segments per the mark spec. Renders nothing when nobody holds the
 * constraint — an empty bar would read as a zero-width mystery rather than
 * "nobody", which the count beside it already says.
 */
export function RatingDistributionBar({
  levelCounts,
  levelNames,
  total,
}: {
  levelCounts: Record<number, number>;
  /** ordinal → admin-editable rung name, for the hover text. */
  levelNames?: Map<number, string>;
  total: number;
}) {
  if (total <= 0) return null;
  return (
    <span className="flex h-1.5 w-14 shrink-0 gap-[2px] overflow-hidden rounded-full">
      {[1, 2, 3, 4, 5].map((level) => {
        const n = levelCounts[level] ?? 0;
        if (n === 0) return null;
        const label = levelNames?.get(level);
        return (
          <span
            key={level}
            className="h-full rounded-full"
            style={{ backgroundColor: ratingColorVar(level), flexGrow: n }}
            title={`${label ? `${label} (${level})` : `Rating ${level}`}: ${n} ${
              n === 1 ? "person" : "people"
            }`}
          />
        );
      })}
    </span>
  );
}
